import { BarChartProps } from '@mui/x-charts';
import { useXScale, useYScale } from '@mui/x-charts/hooks';
import { Box } from '@mui/material';
import React, { useId, useState } from 'react';

import BarChart from 'components/charts/bar-chart';
import ButtonGroup from 'components/button-group';
import { SERIES_HIGHLIGHT } from 'components/charts/constants/chart-defaults';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import DataLabChartTooltip from 'views/playground/features/data-lab/components/data-lab-chart-tooltip';
import { ChartStatus } from 'components/charts/types/chart-palette.type';
import { SelectableChartProps } from 'views/playground/features/data-lab/types/selectable-chart.type';
import { CategoryPoint } from 'views/playground/features/data-lab/types/chart-series.type';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';
import { HEALTH_STATUS_COLOR, healthStatusFor, MORTALITY_THRESHOLDS } from 'views/playground/features/data-lab/utils/health-status';

type Props = SelectableChartProps &
  Pick<BarChartProps, 'height'> & {
    data: CategoryPoint[];
  };

// One continuous blend through the three status colors. Each color peaks mid-band, so a bar
// changes color gradually across each threshold. Beyond the critical threshold the last color continues.
const { watch, critical } = MORTALITY_THRESHOLDS;
const GRADIENT_STOPS: { value: number; color: keyof ChartStatus }[] = [
  { value: watch / 2, color: HEALTH_STATUS_COLOR.good },
  { value: (watch + critical) / 2, color: HEALTH_STATUS_COLOR.watch },
  { value: critical, color: HEALTH_STATUS_COLOR.critical },
];

type Orientation = 'vertical' | 'horizontal';

const SeverityGradient: React.FC<{ id: string; orientation: Orientation }> = ({ id, orientation }) => {
  const palette = useChartPalette();
  const xScale = useXScale();
  const yScale = useYScale();
  const horizontal = orientation === 'horizontal';

  return (
    <defs>
      <linearGradient
        id={id}
        gradientUnits="userSpaceOnUse"
        x1={horizontal ? (xScale(0) ?? 0) : 0}
        x2={horizontal ? (xScale(critical) ?? 0) : 0}
        y1={horizontal ? 0 : (yScale(0) ?? 0)}
        y2={horizontal ? 0 : (yScale(critical) ?? 0)}
      >
        {GRADIENT_STOPS.map(({ value, color }) => (
          <stop key={value} offset={value / critical} stopColor={palette.status[color]} />
        ))}
      </linearGradient>
    </defs>
  );
};

const SeverityBarChart: React.FC<Props> = ({ data, height = 320, selectedId, onSelect }) => {
  const { formatPercent } = useMetricFormatters();
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation();
  const gradientId = `severity-gradient-${useId().replace(/:/g, '')}`;
  const [orientation, setOrientation] = useState<Orientation>('vertical');
  const horizontal = orientation === 'horizontal';

  const labels = data.map((point) => point.label);
  const statusColors = data.map((point) => palette.status[HEALTH_STATUS_COLOR[healthStatusFor(point.value, MORTALITY_THRESHOLDS)]]);
  const categoryAxis = {
    data: labels,
    scaleType: 'band' as const,
    label: 'Cage',
    colorMap: { type: 'ordinal' as const, values: labels, colors: statusColors },
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="flex-end">
        <ButtonGroup
          aria-label="Chart orientation"
          value="orientation"
          buttons={[
            { label: 'Vertical', value: 'left', state: false },
            { label: 'Horizontal', value: 'right', state: true },
          ]}
          handleClick={(isHorizontal) => setOrientation(isHorizontal ? 'horizontal' : 'vertical')}
        />
      </Box>
      <BarChart
        height={height}
        skipAnimation={chartSkipAnimation}
        hideLegend
        layout={orientation}
        xAxis={horizontal ? [{ scaleType: 'linear', min: 0, label: 'Mortality rate (%)' }] : [categoryAxis]}
        yAxis={horizontal ? [{ ...categoryAxis, width: 'auto' }] : [{ scaleType: 'linear', min: 0, label: 'Mortality rate (%)' }]}
        series={[
          {
            id: 'mortality-rate',
            label: 'Mortality rate',
            data: data.map((point) => point.value),
            valueFormatter: (value: number | null) => (value === null ? '' : formatPercent(value)),
            ...SERIES_HIGHLIGHT,
          },
        ]}
        slots={{ tooltip: DataLabChartTooltip }}
        slotProps={{ bar: { fill: `url(#${gradientId})` } }}
        onItemClick={(_event, barItemIdentifier) => {
          const point = data[barItemIdentifier.dataIndex];
          if (!point) return;
          onSelect?.(point.id === selectedId ? null : point.id);
        }}
      >
        <SeverityGradient id={gradientId} orientation={orientation} />
      </BarChart>
    </Box>
  );
};

export default SeverityBarChart;
